import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid, Typography } from '@mui/material';
import { Dictionary, isEmpty, keys, map } from 'lodash';
import React from 'react';

import {
  ClassificationBinningParams,
  InferenceModelData,
  OrchestrationInference,
  SlideInferenceResults,
} from 'interfaces/calculateFeatures';
import { ModelPreferencesById, ModelsByStainByType, ModelUserPreferences } from 'utils/models';
import { OrchestrationBySlideByResultName, OrchestrationBySlideByType } from 'utils/orchestrationSelections';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { BinaryClassifierSelectionMode } from '../../../binaryClassifierSelections';
import InferenceModel from './InferenceModel';

export interface InferenceStainProps {
  studyId: string;
  stain: string;
  stainModelInferences?: ModelsByStainByType[string];
  orchestrationByResultName?: {
    [resultName: string]: OrchestrationInference[];
  };
  slides: Dictionary<SlideInferenceResults>;
  selectedOrchestrations?: OrchestrationBySlideByType;
  setSelectedOrchestrationsByTypeByStain?: (
    slideIds: string[],
    model: InferenceModelData,
    modelType: string,
    orchestration: OrchestrationInference
  ) => void;
  selectedOrchestrationsByResultName?: OrchestrationBySlideByResultName;
  setSelectedOrchestrationsByResultName?: (
    slideIds: string[],
    resultName: string,
    orchestration: OrchestrationInference
  ) => void;
  setModelPreferencesByModelId?: (modelId: string, userPreferences: ModelUserPreferences) => void;
  modelPreferencesByModelId?: ModelPreferencesById;
  setSelectedOrchestrationsByClassificationType: (modelType: string, previousModelType?: string) => void;
  setIntensityClassificationBinning?: (modelId: string, binning: ClassificationBinningParams) => void;
  binaryClassifierSelectionMode?: BinaryClassifierSelectionMode;
  setBinaryClassifierSelectionMode?: (mode: BinaryClassifierSelectionMode) => void;
  enableSpotDetectionSetup?: boolean;
  availableCellSegmentationGeometries?: string[];
  defaultExpanded?: boolean;
  disableIfNoJob?: boolean;
}

const InferenceStain: React.FC<React.PropsWithChildren<InferenceStainProps>> = ({
  studyId,
  stain,
  stainModelInferences,
  orchestrationByResultName,
  slides,
  selectedOrchestrations,
  setSelectedOrchestrationsByTypeByStain: setSelectedOrchestrations,
  selectedOrchestrationsByResultName,
  setSelectedOrchestrationsByResultName,
  modelPreferencesByModelId,
  setModelPreferencesByModelId,
  setSelectedOrchestrationsByClassificationType,
  setIntensityClassificationBinning,
  binaryClassifierSelectionMode,
  setBinaryClassifierSelectionMode,
  enableSpotDetectionSetup = false,
  availableCellSegmentationGeometries,
  defaultExpanded = false,
  disableIfNoJob = false,
}) => {
  const { stainTypeIdToDisplayName, isLoadingStainTypeOptions } = useStainTypeIdToDisplayName();

  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        {isLoadingStainTypeOptions ? (
          <CircularProgress />
        ) : (
          <Typography component="span" fontWeight={700}>
            {stainTypeIdToDisplayName(stain)}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={1}>
          {!isEmpty(stainModelInferences)
            ? map(keys(stainModelInferences), (modelType) => {
                return (
                  <Grid item xs={12} key={modelType}>
                    <InferenceModel
                      key={modelType}
                      stain={stain}
                      modelType={modelType}
                      stainModelInferences={stainModelInferences}
                      slides={slides}
                      selectedOrchestrations={selectedOrchestrations}
                      setSelectedOrchestrations={setSelectedOrchestrations}
                      studyId={studyId}
                      modelPreferencesByModelId={modelPreferencesByModelId}
                      setModelPreferencesByModelId={setModelPreferencesByModelId}
                      setSelectedOrchestrationsByClassificationType={setSelectedOrchestrationsByClassificationType}
                      setIntensityClassificationBinning={setIntensityClassificationBinning}
                      binaryClassifierSelectionMode={binaryClassifierSelectionMode}
                      availableCellSegmentationGeometries={availableCellSegmentationGeometries}
                      enableSpotDetectionSetup={enableSpotDetectionSetup}
                      setBinaryClassifierSelectionMode={setBinaryClassifierSelectionMode}
                      disableIfNoJob={disableIfNoJob}
                    />
                  </Grid>
                );
              })
            : !isEmpty(orchestrationByResultName)
            ? map(orchestrationByResultName, (orchestrations, resultName) => {
                return (
                  <Grid item xs={12} key={resultName}>
                    <InferenceModel
                      key={resultName}
                      stain={stain}
                      resultName={resultName}
                      orchestrations={orchestrations}
                      slides={slides}
                      selectedOrchestrationsByResultName={selectedOrchestrationsByResultName}
                      setSelectedOrchestrationsByResultName={setSelectedOrchestrationsByResultName}
                      studyId={studyId}
                      stainModelInferences={stainModelInferences}
                      modelPreferencesByModelId={modelPreferencesByModelId}
                      setModelPreferencesByModelId={setModelPreferencesByModelId}
                      availableCellSegmentationGeometries={availableCellSegmentationGeometries}
                      enableSpotDetectionSetup={enableSpotDetectionSetup}
                      setIntensityClassificationBinning={setIntensityClassificationBinning}
                      disableIfNoJob={disableIfNoJob}
                    />
                  </Grid>
                );
              })
            : null}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default InferenceStain;
